import { useState } from 'react';
import { BookOpen, Globe, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Language = 'en' | 'he';

// Manual content in both languages
const manualContent = {
  en: {
    title: '📘 System Operational Guide',
    subtitle: 'Thermalink Project Management - User Manual',
    languageLabel: 'English',

    tableOfContents: 'Table of Contents',

    sections: [
      {
        id: 'getting-started',
        title: '1. Getting Started (The Setup Phase)',
        icon: '🚀',
        content: `
### Prerequisites
Before you can start using the system effectively, you must set up the following data in order:

**Step 1: Review Products Catalog**
- Navigate to **Products** in the sidebar
- The system comes pre-loaded with two main assemblies:
  - 📦 **Sensor Box** - Contains sensors and flow switches
  - 📦 **Control Box** - Contains control units and interfaces
- Each assembly has components listed underneath with their **Qty/System** (how many needed per unit)

**Step 2: Set Up Monthly Production Plan**
- Go to **Inventory & Planning** → **Monthly Planning** tab
- Enter the number of Sensor Box and Control Box systems planned for each month
- Add the **SMC PO Number** when you receive the customer order
- Set **Supply Date** for delivery targets

**Step 3: Create Purchase Orders**
- Go to **Procurement Expenses**
- Create POs for the components you need to order
- Mark POs as "Order Received" when confirmed by supplier

### ⚠️ The Golden Rules
1. **Never delete a product that has active stock or is referenced in a PO**
2. **Always update "Used" quantities when components are consumed in production**
3. **Mark monthly plans as "Completed" only after all systems are shipped**
4. **Enter costs and revenue in Project Profit for accurate financial tracking**
        `,
      },
      {
        id: 'products',
        title: '2. Products Module',
        icon: '📦',
        content: `
### The Goal
Manage your product catalog including assemblies (finished goods) and components (parts).

### How to Use

**Viewing Products:**
1. Click **Products** in the sidebar
2. Use the **Tree View** to see hierarchy (assemblies → components)
3. Or use **Tabs View** to filter by Sensor Box or Control Box components

**Product Information:**
- **SKU**: Unique identifier for each item
- **Name**: Descriptive name
- **Manufacturer**: Who makes this part
- **Qty/System**: How many of this component goes into one assembly
- **Unit Cost**: Price per unit (in ₪)

**Adding a New Component:**
1. Click **Add Component** button
2. Fill in SKU, Name, Manufacturer
3. Select **Parent** (Sensor Box or Control Box)
4. Enter **Qty/System** (multiplier for requirements calculation)
5. Click **Save**

### 📊 Status Cards Explained
- **Total Products**: Count of all items in catalog
- **Assemblies**: Number of main product types (2)
- **Components**: Number of parts/materials
        `,
      },
      {
        id: 'inventory',
        title: '3. Inventory & Planning Module',
        icon: '📋',
        content: `
### The Goal
Plan monthly production and track component requirements to ensure you have enough parts.

### How to Use

**Monthly Planning Tab:**
1. Go to **Inventory & Planning** → click **Monthly Planning** tab
2. For each month row:
   - Enter **Sensor Box Systems** quantity
   - Enter **Control Box Systems** quantity
   - When customer confirms, add **SMC PO Number**
   - Set the **Supply Date**
   - Change **Status** to "Completed" when shipped

**Requirements Tab:**
1. Click **Requirements** tab
2. Use **Period Filter** to view specific month or "Full Review"
3. Filter by **Assembly** (Sensor Box / Control Box / All)

**Understanding the Requirements Table:**
| Column | Meaning |
|--------|---------|
| **Required** | Qty/System × Planned Systems |
| **Ordered** | Total quantity in "Order Received" POs |
| **Used** | Quantity consumed in production (editable) |
| **On Hand** | Ordered - Used (current stock) |
| **To Order** | Required - Ordered (shortfall) |

**Updating Used Quantities:**
- In the Requirements table, find the **Used** column
- Enter the quantity that has been consumed
- This updates On Hand automatically

### 📊 Status Cards Explained
- **Total Required**: Sum of all systems planned across months
- **Already Ordered**: Systems with PO numbers entered
- **Need to Order**: Systems without PO numbers
- **Already Supplied**: Systems marked as "Completed"
- **Qty to Supply**: Remaining systems to deliver
        `,
      },
      {
        id: 'procurement',
        title: '4. Procurement Expenses Module',
        icon: '🛒',
        content: `
### The Goal
Create and manage purchase orders for components from suppliers.

### How to Use

**Creating a Purchase Order:**
1. Go to **Procurement Expenses**
2. Click **Create PO** button
3. Fill in:
   - **PO Number**: Your internal reference (e.g., PO-2024-001)
   - **Supplier**: Select from the dropdown
   - **Expected Delivery**: When you expect the parts
4. Add line items:
   - Select **Product** from dropdown
   - Enter **Quantity** to order
5. Click **Create PO**

**PO Status Workflow:**
1. **In Progress** → PO is being drafted
2. **Order Received** → Supplier confirmed the order ✅
   - ⚠️ This adds to "Ordered" in Requirements!
3. **Cancelled** → Order was cancelled

**Managing POs:**
- Click the **⋯** menu on any PO row
- **View Details**: See all line items
- **Edit**: Modify PO (only when In Progress)
- **Mark as Order Received**: Confirm supplier accepted
- **Revert to In Progress**: Undo order confirmation
- **Cancel PO**: Cancel the order

### 📊 Status Cards Explained
- **Open POs**: Number of active (not closed/cancelled) POs
- **Total Open Value**: Sum of all open PO amounts
- **Pending Receival**: POs waiting for delivery
- **In Progress**: Draft POs not yet sent
        `,
      },
      {
        id: 'quality',
        title: '5. Quality Module',
        icon: '✅',
        content: `
### The Goal
Track test results and manage non-conformance reports (NCRs) for quality control.

### How to Use

**Recording Test Results:**
1. Go to **Quality**
2. Click **Record Test** button
3. Select the **Work Order** being tested
4. Enter **Serial Number** of the unit
5. Select **Test Type** (Functional, Thermal, Electrical, Visual)
6. Record **Result** (Pass, Fail, Conditional)
7. Enter measured and expected values
8. Add notes if needed

**Creating NCR Reports:**
1. Click **Create NCR** button
2. Select the **Product** with the issue
3. Choose **Severity** (Minor, Major, Critical)
4. Select **Defect Type** and describe the issue
5. Choose **Disposition**:
   - Use As Is
   - Rework
   - Scrap
   - Return to Supplier

### 📊 Status Cards Explained
- **Pass Rate**: Percentage of tests that passed
- **Tests Passed**: Number of successful tests
- **Tests Failed**: Number of failed tests (needs attention!)
- **Open NCRs**: Non-conformances pending resolution
- **Critical NCRs**: High-severity issues requiring urgent action
        `,
      },
      {
        id: 'profit',
        title: '6. Project Profit Module',
        icon: '💰',
        content: `
### The Goal
Track project revenue and costs to monitor profitability.

### How to Use

**Adding Revenue Entry:**
1. Go to **Project Profit**
2. Click **Add Entry**
3. Select **Revenue** type
4. Choose category (Product Sales, Service Revenue, etc.)
5. Enter amount and date
6. Add description (optional)

**Adding Cost Entry:**
1. Click **Add Entry**
2. Select **Cost** type
3. Choose category (Materials, Labor, Shipping, etc.)
4. Enter amount and date

**Filtering by Time:**
- Use the time range dropdown (1 Month, 3 Months, 6 Months, All Time)

**Editing/Deleting Entries:**
- Click **⋯** menu on any entry row
- Select **Edit** or **Delete**

### 📊 Status Cards Explained
- **Total Revenue**: Sum of all revenue entries
- **Total Costs**: Sum of all cost entries
- **Net Profit**: Revenue minus Costs
- **Profit Margin**: (Net Profit / Revenue) × 100%
        `,
      },
      {
        id: 'gantt',
        title: '7. Gantt View Module',
        icon: '📅',
        content: `
### The Goal
Visualize project timeline with orders and their production tasks.

### How to Use

**Viewing the Timeline:**
1. Go to **Gantt View**
2. Each row represents an order
3. Tasks are shown as bars on the timeline
4. Progress is indicated by the filled portion of each bar

**Adding Orders and Tasks:**
1. Click **Add Order** to create a new production order
2. Enter Order Number and Product Name
3. Add tasks with:
   - Task name
   - Start and End dates
   - Progress percentage (0-100%)

**Updating Progress:**
- Click on a task to edit
- Update the progress percentage as work completes

### 📊 Dashboard Summary
The Dashboard shows Gantt statistics:
- Total Orders
- Total Tasks
- Completed Tasks
- In Progress Tasks
- Overall Progress percentage
        `,
      },
      {
        id: 'happy-flow',
        title: '8. The "Happy Flow" (End-to-End Example)',
        icon: '🎯',
        content: `
### Complete Scenario: New NVIDIA Order

**Scenario**: NVIDIA orders 10 Sensor Box systems and 10 Control Box systems for delivery in March 2025.

---

**Step 1: Enter Monthly Plan**
1. Go to **Inventory & Planning** → **Monthly Planning**
2. Find the March 2025 row
3. Enter: Sensor Box = 10, Control Box = 10
4. Enter SMC PO Number when received from customer
5. Set Supply Date to the delivery target

---

**Step 2: Review Requirements**
1. Switch to **Requirements** tab
2. Select "March 2025" from the period filter
3. Check the **To Order** column for each component
4. Items with To Order > 0 need to be purchased

---

**Step 3: Create Purchase Orders**
1. Go to **Procurement Expenses**
2. Click **Create PO**
3. Select supplier (e.g., SMC for SMC parts)
4. Add line items for components that need ordering
5. Save the PO
6. When supplier confirms, click **Mark as Order Received**

---

**Step 4: Track Production**
1. As components arrive, they're now "Ordered"
2. When production uses parts, update **Used** in Requirements
3. Go to **Gantt View** to track assembly progress
4. Update task progress as work completes

---

**Step 5: Quality Control**
1. Go to **Quality** module
2. Record test results for each assembled unit
3. Create NCR if any issues found
4. Resolve NCRs before shipping

---

**Step 6: Complete and Ship**
1. When all units pass QC, prepare for shipping
2. Go to **Inventory & Planning** → **Monthly Planning**
3. Mark the month's Status as "Completed"

---

**Step 7: Record Financials**
1. Go to **Project Profit**
2. Add **Revenue** entry for the sale
3. Add **Cost** entries for materials, labor, shipping
4. Review Net Profit and Margin
        `,
      },
      {
        id: 'troubleshooting',
        title: '9. Troubleshooting & FAQ',
        icon: '❓',
        content: `
### Common Questions

**Q: Why is "To Order" showing a negative number?**
> A: You've ordered more than required. This is okay - it means you have buffer stock.

**Q: Why isn't my PO updating the "Ordered" column?**
> A: Only POs with status "Order Received" count toward Ordered quantities. Make sure to mark your PO as received.

**Q: How do I correct a wrong "Used" quantity?**
> A: Go to **Inventory & Planning** → **Requirements** tab, find the component, and edit the Used field directly.

**Q: Why can't I delete a product?**
> A: Products with related PO lines or inventory records cannot be deleted. Archive them instead by setting them inactive.

**Q: The On Hand shows negative - what does this mean?**
> A: You've used more components than you've ordered. Either:
> - Update your POs to reflect actual orders
> - Adjust the Used quantity if it's incorrect

**Q: How do I split an order across months?**
> A: Enter partial quantities in each month's row. For example, if customer orders 20 units over 2 months, put 10 in each month.

**Q: What's the difference between "Required" and "To Order"?**
> - **Required** = Total components needed (Qty/System × Systems)
> - **To Order** = Required minus what's already Ordered (the gap)

**Q: How do I track partial deliveries from suppliers?**
> A: Create separate POs for each delivery, or track partial receipts by updating the Used/Ordered quantities manually.

### ⚠️ Common Mistakes to Avoid

1. **Forgetting to mark POs as "Order Received"**
   - Result: Requirements show incorrect "To Order" amounts

2. **Not updating "Used" when consuming components**
   - Result: On Hand shows inflated numbers

3. **Marking monthly plan as "Completed" before actually shipping**
   - Result: Dashboard shows incorrect supply statistics

4. **Creating POs without proper supplier selection**
   - Result: Difficult to track vendor performance

5. **Not recording costs in Project Profit**
   - Result: Profit margins appear higher than reality
        `,
      },
    ],
  },
  he: {
    title: '📘 מדריך תפעול המערכת',
    subtitle: 'Thermalink Project Management - מדריך למשתמש',
    languageLabel: 'עברית',

    tableOfContents: 'תוכן עניינים',

    sections: [
      {
        id: 'getting-started',
        title: '1. תחילת העבודה (שלב ההגדרות)',
        icon: '🚀',
        content: `
### דרישות מוקדמות
לפני שתוכל להתחיל להשתמש במערכת ביעילות, עליך להגדיר את הנתונים הבאים לפי הסדר:

**שלב 1: סקירת קטלוג המוצרים**
- נווט אל **Products** בתפריט הצד
- המערכת מגיעה עם שתי הרכבות עיקריות:
  - 📦 **Sensor Box** - מכיל חיישנים ומפסקי זרימה
  - 📦 **Control Box** - מכיל יחידות בקרה וממשקים
- לכל הרכבה יש רכיבים עם **Qty/System** (כמה נדרש ליחידה)

**שלב 2: הגדרת תוכנית ייצור חודשית**
- לך ל-**Inventory & Planning** → לשונית **Monthly Planning**
- הזן את מספר מערכות ה-Sensor Box ו-Control Box המתוכננות לכל חודש
- הוסף את **SMC PO Number** כשמתקבלת הזמנת הלקוח
- הגדר את **Supply Date** ליעדי אספקה

**שלב 3: יצירת הזמנות רכש**
- לך ל-**Procurement Expenses**
- צור הזמנות רכש (PO) לרכיבים שצריך להזמין
- סמן PO כ-"Order Received" כשהספק מאשר

### ⚠️ כללי הזהב
1. **לעולם אל תמחק מוצר שיש לו מלאי פעיל או מופיע ב-PO**
2. **תמיד עדכן כמויות "Used" כשרכיבים נצרכים בייצור**
3. **סמן תוכניות חודשיות כ-"Completed" רק אחרי שכל המערכות נשלחו**
4. **הזן עלויות והכנסות ב-Project Profit למעקב פיננסי מדויק**
        `,
      },
      {
        id: 'products',
        title: '2. מודול מוצרים',
        icon: '📦',
        content: `
### המטרה
ניהול קטלוג המוצרים כולל הרכבות (מוצרים מוגמרים) ורכיבים (חלקים).

### אופן השימוש

**צפייה במוצרים:**
1. לחץ על **Products** בתפריט הצד
2. השתמש ב-**Tree View** לראות היררכיה (הרכבות → רכיבים)
3. או השתמש ב-**Tabs View** לסינון לפי רכיבי Sensor Box או Control Box

**מידע על מוצר:**
- **SKU**: מזהה ייחודי לכל פריט
- **Name**: שם תיאורי
- **Manufacturer**: מי מייצר את החלק
- **Qty/System**: כמה מרכיב זה נכנס להרכבה אחת
- **Unit Cost**: מחיר ליחידה (ב-₪)

**הוספת רכיב חדש:**
1. לחץ על כפתור **Add Component**
2. מלא SKU, Name, Manufacturer
3. בחר **Parent** (Sensor Box או Control Box)
4. הזן **Qty/System** (מכפיל לחישוב דרישות)
5. לחץ **Save**

### 📊 הסבר כרטיסי הסטטוס
- **Total Products**: ספירת כל הפריטים בקטלוג
- **Assemblies**: מספר סוגי המוצרים העיקריים (2)
- **Components**: מספר החלקים/חומרים
        `,
      },
      {
        id: 'inventory',
        title: '3. מודול מלאי ותכנון',
        icon: '📋',
        content: `
### המטרה
תכנון ייצור חודשי ומעקב אחר דרישות רכיבים כדי להבטיח שיש מספיק חלקים.

### אופן השימוש

**לשונית תכנון חודשי:**
1. לך ל-**Inventory & Planning** → לחץ על לשונית **Monthly Planning**
2. לכל שורת חודש:
   - הזן כמות **Sensor Box Systems**
   - הזן כמות **Control Box Systems**
   - כשהלקוח מאשר, הוסף **SMC PO Number**
   - הגדר את **Supply Date**
   - שנה **Status** ל-"Completed" כשנשלח

**לשונית דרישות:**
1. לחץ על לשונית **Requirements**
2. השתמש ב-**Period Filter** לצפייה בחודש ספציפי או "Full Review"
3. סנן לפי **Assembly** (Sensor Box / Control Box / All)

**הבנת טבלת הדרישות:**
| עמודה | משמעות |
|--------|---------|
| **Required** | Qty/System × מערכות מתוכננות |
| **Ordered** | סה"כ כמות ב-PO עם "Order Received" |
| **Used** | כמות שנצרכה בייצור (ניתן לעריכה) |
| **On Hand** | Ordered - Used (מלאי נוכחי) |
| **To Order** | Required - Ordered (חוסר) |

**עדכון כמויות שנצרכו:**
- בטבלת הדרישות, מצא את עמודת **Used**
- הזן את הכמות שנצרכה
- זה מעדכן את On Hand אוטומטית

### 📊 הסבר כרטיסי הסטטוס
- **Total Required**: סכום כל המערכות המתוכננות בכל החודשים
- **Already Ordered**: מערכות עם מספרי PO שהוזנו
- **Need to Order**: מערכות ללא מספרי PO
- **Already Supplied**: מערכות שסומנו כ-"Completed"
- **Qty to Supply**: מערכות שנותרו לאספקה
        `,
      },
      {
        id: 'procurement',
        title: '4. מודול הוצאות רכש',
        icon: '🛒',
        content: `
### המטרה
יצירה וניהול הזמנות רכש לרכיבים מספקים.

### אופן השימוש

**יצירת הזמנת רכש:**
1. לך ל-**Procurement Expenses**
2. לחץ על כפתור **Create PO**
3. מלא:
   - **PO Number**: מספר הפניה פנימי שלך (לדוגמה: PO-2024-001)
   - **Supplier**: בחר מהרשימה הנפתחת
   - **Expected Delivery**: מתי אתה מצפה לחלקים
4. הוסף שורות פריטים:
   - בחר **Product** מהרשימה
   - הזן **Quantity** להזמנה
5. לחץ **Create PO**

**תהליך סטטוס PO:**
1. **In Progress** → ה-PO בכתיבה
2. **Order Received** → הספק אישר את ההזמנה ✅
   - ⚠️ זה מוסיף ל-"Ordered" בדרישות!
3. **Cancelled** → ההזמנה בוטלה

**ניהול הזמנות:**
- לחץ על תפריט **⋯** בכל שורת PO
- **View Details**: ראה כל שורות הפריטים
- **Edit**: ערוך PO (רק כש-In Progress)
- **Mark as Order Received**: אשר שהספק קיבל
- **Revert to In Progress**: בטל אישור הזמנה
- **Cancel PO**: בטל את ההזמנה

### 📊 הסבר כרטיסי הסטטוס
- **Open POs**: מספר הזמנות פעילות (לא סגורות/מבוטלות)
- **Total Open Value**: סכום כל ההזמנות הפתוחות
- **Pending Receival**: הזמנות שממתינות לאספקה
- **In Progress**: הזמנות בטיוטה שעדיין לא נשלחו
        `,
      },
      {
        id: 'quality',
        title: '5. מודול איכות',
        icon: '✅',
        content: `
### המטרה
מעקב אחר תוצאות בדיקות וניהול דוחות אי-התאמה (NCR) לבקרת איכות.

### אופן השימוש

**רישום תוצאות בדיקה:**
1. לך ל-**Quality**
2. לחץ על כפתור **Record Test**
3. בחר את **Work Order** הנבדק
4. הזן **Serial Number** של היחידה
5. בחר **Test Type** (Functional, Thermal, Electrical, Visual)
6. רשום **Result** (Pass, Fail, Conditional)
7. הזן ערכים נמדדים וצפויים
8. הוסף הערות במידת הצורך

**יצירת דוחות NCR:**
1. לחץ על כפתור **Create NCR**
2. בחר את **Product** עם הבעיה
3. בחר **Severity** (Minor, Major, Critical)
4. בחר **Defect Type** ותאר את הבעיה
5. בחר **Disposition**:
   - Use As Is (שימוש כמות שהוא)
   - Rework (עיבוד מחדש)
   - Scrap (גרוטאה)
   - Return to Supplier (החזרה לספק)

### 📊 הסבר כרטיסי הסטטוס
- **Pass Rate**: אחוז הבדיקות שעברו
- **Tests Passed**: מספר בדיקות מוצלחות
- **Tests Failed**: מספר בדיקות שנכשלו (דורש טיפול!)
- **Open NCRs**: אי-התאמות הממתינות לפתרון
- **Critical NCRs**: בעיות חמורות הדורשות פעולה דחופה
        `,
      },
      {
        id: 'profit',
        title: '6. מודול רווחיות פרויקט',
        icon: '💰',
        content: `
### המטרה
מעקב אחר הכנסות והוצאות הפרויקט לניטור רווחיות.

### אופן השימוש

**הוספת רשומת הכנסה:**
1. לך ל-**Project Profit**
2. לחץ על **Add Entry**
3. בחר סוג **Revenue**
4. בחר קטגוריה (Product Sales, Service Revenue וכו')
5. הזן סכום ותאריך
6. הוסף תיאור (אופציונלי)

**הוספת רשומת עלות:**
1. לחץ על **Add Entry**
2. בחר סוג **Cost**
3. בחר קטגוריה (Materials, Labor, Shipping וכו')
4. הזן סכום ותאריך

**סינון לפי זמן:**
- השתמש ברשימה הנפתחת של טווח זמן (חודש, 3 חודשים, 6 חודשים, הכל)

**עריכה/מחיקת רשומות:**
- לחץ על תפריט **⋯** בכל שורת רשומה
- בחר **Edit** או **Delete**

### 📊 הסבר כרטיסי הסטטוס
- **Total Revenue**: סכום כל רשומות ההכנסה
- **Total Costs**: סכום כל רשומות העלות
- **Net Profit**: הכנסות פחות עלויות
- **Profit Margin**: (רווח נקי / הכנסות) × 100%
        `,
      },
      {
        id: 'gantt',
        title: '7. מודול תצוגת גנט',
        icon: '📅',
        content: `
### המטרה
הדמיה של לוח זמנים הפרויקט עם הזמנות ומשימות הייצור שלהן.

### אופן השימוש

**צפייה בלוח הזמנים:**
1. לך ל-**Gantt View**
2. כל שורה מייצגת הזמנה
3. משימות מוצגות כעמודות על ציר הזמן
4. ההתקדמות מסומנת על ידי החלק הממולא של כל עמודה

**הוספת הזמנות ומשימות:**
1. לחץ על **Add Order** ליצירת הזמנת ייצור חדשה
2. הזן מספר הזמנה ושם מוצר
3. הוסף משימות עם:
   - שם משימה
   - תאריכי התחלה וסיום
   - אחוז התקדמות (0-100%)

**עדכון התקדמות:**
- לחץ על משימה לעריכה
- עדכן את אחוז ההתקדמות ככל שהעבודה מתקדמת

### 📊 סיכום לוח המחוונים
לוח המחוונים מציג סטטיסטיקות גנט:
- סה"כ הזמנות
- סה"כ משימות
- משימות שהושלמו
- משימות בתהליך
- אחוז התקדמות כולל
        `,
      },
      {
        id: 'happy-flow',
        title: '8. "התהליך המושלם" (דוגמה מקצה לקצה)',
        icon: '🎯',
        content: `
### תרחיש מלא: הזמנה חדשה מ-NVIDIA

**תרחיש**: NVIDIA מזמינה 10 מערכות Sensor Box ו-10 מערכות Control Box לאספקה במרץ 2025.

---

**שלב 1: הזנת תוכנית חודשית**
1. לך ל-**Inventory & Planning** → **Monthly Planning**
2. מצא את שורת מרץ 2025
3. הזן: Sensor Box = 10, Control Box = 10
4. הזן SMC PO Number כשמתקבל מהלקוח
5. הגדר Supply Date ליעד האספקה

---

**שלב 2: סקירת דרישות**
1. עבור ללשונית **Requirements**
2. בחר "March 2025" ממסנן התקופה
3. בדוק את עמודת **To Order** לכל רכיב
4. פריטים עם To Order > 0 צריכים להירכש

---

**שלב 3: יצירת הזמנות רכש**
1. לך ל-**Procurement Expenses**
2. לחץ על **Create PO**
3. בחר ספק (לדוגמה: SMC לחלקי SMC)
4. הוסף שורות פריטים לרכיבים שצריך להזמין
5. שמור את ה-PO
6. כשהספק מאשר, לחץ על **Mark as Order Received**

---

**שלב 4: מעקב ייצור**
1. כשרכיבים מגיעים, הם עכשיו "Ordered"
2. כשהייצור משתמש בחלקים, עדכן **Used** בדרישות
3. לך ל-**Gantt View** לעקוב אחר התקדמות ההרכבה
4. עדכן התקדמות משימות ככל שהעבודה מתקדמת

---

**שלב 5: בקרת איכות**
1. לך למודול **Quality**
2. רשום תוצאות בדיקה לכל יחידה שהורכבה
3. צור NCR אם נמצאו בעיות
4. פתור NCRs לפני המשלוח

---

**שלב 6: השלמה ומשלוח**
1. כשכל היחידות עוברות QC, הכן למשלוח
2. לך ל-**Inventory & Planning** → **Monthly Planning**
3. סמן את סטטוס החודש כ-"Completed"

---

**שלב 7: רישום פיננסי**
1. לך ל-**Project Profit**
2. הוסף רשומת **Revenue** למכירה
3. הוסף רשומות **Cost** לחומרים, עבודה, משלוח
4. סקור Net Profit ו-Margin
        `,
      },
      {
        id: 'troubleshooting',
        title: '9. פתרון בעיות ושאלות נפוצות',
        icon: '❓',
        content: `
### שאלות נפוצות

**ש: למה "To Order" מציג מספר שלילי?**
> ת: הזמנת יותר מהנדרש. זה בסדר - זה אומר שיש לך מלאי ביטחון.

**ש: למה ה-PO שלי לא מעדכן את עמודת "Ordered"?**
> ת: רק הזמנות עם סטטוס "Order Received" נספרות לכמויות Ordered. וודא שסימנת את ה-PO שלך כנתקבל.

**ש: איך אני מתקן כמות "Used" שגויה?**
> ת: לך ל-**Inventory & Planning** → לשונית **Requirements**, מצא את הרכיב, וערוך את שדה Used ישירות.

**ש: למה אני לא יכול למחוק מוצר?**
> ת: מוצרים עם שורות PO קשורות או רשומות מלאי לא ניתנים למחיקה. ארכב אותם במקום על ידי הגדרתם כלא פעילים.

**ש: On Hand מציג שלילי - מה זה אומר?**
> ת: השתמשת ביותר רכיבים ממה שהזמנת. או:
> - עדכן את ה-POs שלך לשקף הזמנות בפועל
> - התאם את כמות Used אם היא שגויה

**ש: איך אני מפצל הזמנה בין חודשים?**
> ת: הזן כמויות חלקיות בכל שורת חודש. לדוגמה, אם לקוח מזמין 20 יחידות לאורך 2 חודשים, שים 10 בכל חודש.

**ש: מה ההבדל בין "Required" ל-"To Order"?**
> - **Required** = סה"כ רכיבים נדרשים (Qty/System × מערכות)
> - **To Order** = Required פחות מה שכבר Ordered (הפער)

**ש: איך אני עוקב אחר אספקות חלקיות מספקים?**
> ת: צור הזמנות נפרדות לכל אספקה, או עקוב אחר קבלות חלקיות על ידי עדכון ידני של כמויות Used/Ordered.

### ⚠️ טעויות נפוצות להימנע מהן

1. **שכחה לסמן PO כ-"Order Received"**
   - תוצאה: דרישות מציגות כמויות "To Order" שגויות

2. **אי עדכון "Used" כשצורכים רכיבים**
   - תוצאה: On Hand מציג מספרים מנופחים

3. **סימון תוכנית חודשית כ-"Completed" לפני משלוח בפועל**
   - תוצאה: לוח המחוונים מציג סטטיסטיקות אספקה שגויות

4. **יצירת PO ללא בחירת ספק נכונה**
   - תוצאה: קשה לעקוב אחר ביצועי ספק

5. **אי רישום עלויות ב-Project Profit**
   - תוצאה: מרווחי רווח נראים גבוהים מהמציאות
        `,
      },
    ],
  },
};

// Section component with collapsible content
function ManualSection({
  section,
  isRtl
}: {
  section: typeof manualContent.en.sections[0];
  isRtl: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card className="mb-4">
      <CardHeader
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className={cn(
          "flex items-center gap-3 text-lg",
          isRtl && "flex-row-reverse text-right"
        )}>
          <span className="text-2xl">{section.icon}</span>
          <span className="flex-1">{section.title}</span>
          {isOpen ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform" />
          )}
        </CardTitle>
      </CardHeader>
      {isOpen && (
        <CardContent className={cn(
          "prose prose-sm dark:prose-invert max-w-none",
          isRtl && "text-right"
        )}>
          <div
            className="whitespace-pre-wrap"
            style={{ direction: isRtl ? 'rtl' : 'ltr' }}
            dangerouslySetInnerHTML={{
              __html: section.content
                .replace(/### (.*)/g, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\n- /g, '<br/>• ')
                .replace(/\n\d+\. /g, (match) => `<br/>${match.trim()} `)
                .replace(/\n> (.*)/g, '<blockquote class="border-l-4 border-primary/50 pl-4 my-2 text-muted-foreground italic">$1</blockquote>')
                .replace(/\| (.*) \|/g, '<span class="font-mono text-sm bg-muted px-1 rounded">$1</span>')
                .replace(/---/g, '<hr class="my-4 border-muted"/>')
                .replace(/`(.*?)`/g, '<code class="bg-muted px-1 rounded text-sm">$1</code>')
            }}
          />
        </CardContent>
      )}
    </Card>
  );
}

export function UserManualPage() {
  const [language, setLanguage] = useState<Language>('en');
  const content = manualContent[language];
  const isRtl = language === 'he';

  return (
    <div className={cn("space-y-6", isRtl && "text-right")} dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className={cn("flex items-center gap-3", isRtl && "flex-row-reverse")}>
          <div className={cn(
            "flex items-center justify-center w-12 h-12 rounded-xl",
            "bg-gradient-to-br from-blue-500/20 to-purple-500/20",
            "border border-blue-500/20"
          )}>
            <BookOpen className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">{content.title}</h2>
            <p className="text-muted-foreground">{content.subtitle}</p>
          </div>
        </div>

        {/* Language Toggle */}
        <div className={cn("flex items-center gap-2", isRtl && "flex-row-reverse")}>
          <Globe className="h-4 w-4 text-muted-foreground" />
          <div className="flex rounded-lg border overflow-hidden">
            <Button
              variant={language === 'en' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none"
              onClick={() => setLanguage('en')}
            >
              English
            </Button>
            <Button
              variant={language === 'he' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none"
              onClick={() => setLanguage('he')}
            >
              עברית
            </Button>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <Card>
        <CardHeader>
          <CardTitle className={cn(
            "flex items-center gap-2",
            isRtl && "flex-row-reverse"
          )}>
            📑 {content.tableOfContents}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={cn(
            "grid gap-2 sm:grid-cols-2 lg:grid-cols-3",
            isRtl && "text-right"
          )}>
            {content.sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={cn(
                  "flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors",
                  isRtl && "flex-row-reverse"
                )}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <span>{section.icon}</span>
                <span className="text-sm">{section.title}</span>
              </a>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Manual Sections */}
      <div className="space-y-4">
        {content.sections.map((section) => (
          <div key={section.id} id={section.id}>
            <ManualSection section={section} isRtl={isRtl} />
          </div>
        ))}
      </div>
    </div>
  );
}
